/* storybook-check-ignore */
import { useEffect, useState } from 'react';
import { Flex, Text } from '@opendoor/bricks/core';
import { Listing } from '../../../declarations/exclusives/listing';
import AccountConnector, { AccountProps } from '../../shared/AccountConnector';
import moment, { Duration } from 'moment-timezone';
import { ListingStates } from '../constants/constants';
import { AnimatePresence, motion } from 'framer-motion';

// The setup for moment-duration-format should be only made where it's being used
// otherwise we would be sending a bigger JS than we need for some pages.
// eslint-disable-next-line @typescript-eslint/no-var-requires
const momentDurationFormatSetup = require('moment-duration-format');
momentDurationFormatSetup(moment);

type Props = {
  listing: Listing & { el_state: ListingStates };
  reservedMessage?: string;
} & AccountProps;

const RESERVED_MESSAGE =
  'A buyer is currently setting up a contract on this home. Add to watchlist to get notified if this home becomes available to buy.';

const MotionFlex = motion(Flex);

function ReservedHomeHeader({ listing, reservedMessage = RESERVED_MESSAGE, ...account }: Props) {
  const [remainingContractReservationDuration, setRemainingContractReservationDuration] =
    useState<Duration>();

  const isReservedHome = listing.el_state === ListingStates.RESERVATION_PENDING;
  const isActiveReservationCustomer =
    isReservedHome &&
    !account.infoIsLoading &&
    account.loggedIn &&
    account.human?.get('customer_uuid') == listing.exclusive_listing_info.reservation_customer_uuid;
  useEffect(() => {
    if (isActiveReservationCustomer && listing.exclusive_listing_info.reservation_end_time) {
      const timeout = setTimeout(() => {
        const diff = moment(listing.exclusive_listing_info.reservation_end_time).diff(moment());
        if (diff > 0) {
          setRemainingContractReservationDuration(moment.duration(diff, 'ms'));
        }
      }, 1000);
      return () => clearTimeout(timeout);
    }
    return;
  }, [
    isActiveReservationCustomer,
    listing.exclusive_listing_info.reservation_end_time,
    remainingContractReservationDuration,
  ]);

  const isNotVisible = !isReservedHome || account.infoIsLoading;

  return (
    <AnimatePresence presenceAffectsLayout>
      {!isNotVisible && (
        <MotionFlex
          as="header"
          width="100%"
          alignItems="center"
          justifyContent="center"
          background={isActiveReservationCustomer ? 'green50' : 'red50'}
          paddingX={['1.5rem', '1.5rem', '1.5rem', '2rem']}
          paddingY={'0.5rem'}
          overflow={'hidden'}
          zIndex={7}
          initial={{ height: 0 }}
          animate={{ height: 'auto' }}
          transition={{ default: { duration: 0.3, type: 'easeInOut' } }}
        >
          <Text color="neutrals0" fontWeight="semibold" fontSize={['s00', 's0']} textAlign="center">
            {isActiveReservationCustomer &&
              (remainingContractReservationDuration
                ? remainingContractReservationDuration.format(
                    '[Home held for] D [day] [:] H [hour] [:] m [min] [:] s [sec]',
                  )
                : 'Home held')}
            {!isActiveReservationCustomer && reservedMessage}
          </Text>
        </MotionFlex>
      )}
    </AnimatePresence>
  );
}

export default AccountConnector(ReservedHomeHeader);
